import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Trash2, CalendarClock, ShieldAlert, AlertTriangle, Mail, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { atoDebtAPI, ATO_DEBT_TYPE_LABELS, ATO_DEBT_STATUS_COLORS, RISK_LEVEL_COLORS, CORRESPONDENCE_TYPE_LABELS } from '../lib/atoDebtAPI';
import { formatAUDate } from '../lib/dateUtils';

const fmtMoney = (n) => `$${(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const instColors = { pending: 'bg-muted text-foreground/90', paid: 'bg-green-100 text-green-700', overdue: 'bg-red-100 text-red-700', missed: 'bg-red-100 text-red-700' };

export default function ATODebtDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [debt, setDebt] = useState(null);
  const [plan, setPlan] = useState(null);
  const [instalments, setInstalments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planForm, setPlanForm] = useState({ instalment_amount: '', frequency: 'monthly', start_date: '', number_of_instalments: '' });
  const [risk, setRisk] = useState(null);
  const [correspondence, setCorrespondence] = useState([]);
  const [corrForm, setCorrForm] = useState({ direction: 'incoming', correspondence_type: 'notice', subject: '', summary: '', occurred_on: '', reference: '' });
  const [showCorrForm, setShowCorrForm] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, pRes, rRes, cRes] = await Promise.all([
        atoDebtAPI.get(id),
        atoDebtAPI.getPaymentPlan(id),
        atoDebtAPI.getRisk(id),
        atoDebtAPI.listCorrespondence(id),
      ]);
      setDebt(dRes.data);
      setPlan(pRes.data.plan);
      setInstalments(pRes.data.instalments || []);
      setRisk(rRes.data);
      setCorrespondence(cRes.data.correspondence || []);
    } catch (error) {
      toast.error('Failed to load debt');
      navigate('/ato-debt');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCorrespondence = async (e) => {
    e.preventDefault();
    if (!corrForm.subject.trim()) { toast.error('Subject is required'); return; }
    try {
      await atoDebtAPI.addCorrespondence(id, corrForm);
      toast.success('Correspondence logged');
      setCorrForm({ direction: 'incoming', correspondence_type: 'notice', subject: '', summary: '', occurred_on: '', reference: '' });
      setShowCorrForm(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log correspondence');
    }
  };

  const handleDeleteCorrespondence = async (corrId) => {
    try {
      await atoDebtAPI.deleteCorrespondence(id, corrId);
      load();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    if (!planForm.instalment_amount || !planForm.start_date || !planForm.number_of_instalments) {
      toast.error('Fill in all payment plan fields');
      return;
    }
    try {
      await atoDebtAPI.createPaymentPlan(id, {
        total_amount: debt.balance,
        instalment_amount: parseFloat(planForm.instalment_amount),
        frequency: planForm.frequency,
        start_date: planForm.start_date,
        number_of_instalments: parseInt(planForm.number_of_instalments, 10),
      });
      toast.success('Payment plan created');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create plan');
    }
  };

  const handleInstalment = async (instId, status) => {
    try {
      await atoDebtAPI.updateInstalment(instId, status);
      load();
    } catch (error) {
      toast.error('Failed to update instalment');
    }
  };

  const handleDelete = async () => {
    try {
      await atoDebtAPI.delete(id);
      toast.success('Debt deleted');
      navigate('/ato-debt');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading || !debt) {
    return <div className="p-6"><div className="animate-pulse h-64 bg-muted rounded"></div></div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/ato-debt')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to ATO Debt
        </Button>
        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={handleDelete}>
          <Trash2 className="w-4 h-4 mr-2" /> Delete
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="debt-detail-title">
          {ATO_DEBT_TYPE_LABELS[debt.debt_type] || debt.debt_type}
        </h1>
        <Badge className={`${ATO_DEBT_STATUS_COLORS[debt.status]} capitalize`}>{debt.status?.replace('_', ' ')}</Badge>
      </div>
      <p className="text-muted-foreground -mt-3">{debt.client_name}{debt.due_date ? ` · due ${formatAUDate(debt.due_date)}` : ''}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card><CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Original Amount</p>
          <p className="text-2xl font-bold text-foreground mt-1">{fmtMoney(debt.original_amount)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Current Balance</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{fmtMoney(debt.balance)}</p>
        </CardContent></Card>
      </div>

      {/* B#18 — DPN / Risk assessment */}
      {risk && (
        <Card data-testid="debt-risk-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              <ShieldAlert className="w-5 h-5" /> Risk Assessment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge className={`${RISK_LEVEL_COLORS[risk.level] || ''} capitalize text-sm px-3 py-1`}>
                {risk.level} risk
              </Badge>
              <span className="text-sm text-muted-foreground">Score: <span className="font-semibold text-foreground">{risk.score}/100</span></span>
              {risk.dpn_exposure && (
                <Badge className="bg-red-600 text-white flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> DPN Exposure</Badge>
              )}
            </div>
            {risk.dpn_reason && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 flex gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{risk.dpn_reason}</span>
              </div>
            )}
            {risk.factors?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Contributing factors</p>
                <ul className="text-sm text-foreground/90 list-disc list-inside space-y-0.5">
                  {risk.factors.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payment plan */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <CalendarClock className="w-5 h-5" /> Payment Plan
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!plan ? (
            <form onSubmit={handleCreatePlan} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs">Instalment amount</Label>
                <Input type="number" step="0.01" value={planForm.instalment_amount} onChange={(e) => setPlanForm({ ...planForm, instalment_amount: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Frequency</Label>
                <Select value={planForm.frequency} onValueChange={(v) => setPlanForm({ ...planForm, frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="fortnightly">Fortnightly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Start date</Label>
                <Input type="date" value={planForm.start_date} onChange={(e) => setPlanForm({ ...planForm, start_date: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs"># Instalments</Label>
                <div className="flex gap-2">
                  <Input type="number" value={planForm.number_of_instalments} onChange={(e) => setPlanForm({ ...planForm, number_of_instalments: e.target.value })} />
                  <Button type="submit" className="bg-primary hover:bg-primary/90">Create</Button>
                </div>
              </div>
            </form>
          ) : (
            <>
              <div className="flex flex-wrap gap-4 text-sm mb-4">
                <span className="text-muted-foreground">Instalment: <span className="font-medium text-foreground">{fmtMoney(plan.instalment_amount)}</span></span>
                <span className="text-muted-foreground">Frequency: <span className="font-medium text-foreground capitalize">{plan.frequency}</span></span>
                <span className="text-muted-foreground">Count: <span className="font-medium text-foreground">{plan.number_of_instalments}</span></span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {instalments.map((inst) => (
                    <TableRow key={inst.id} data-testid={`instalment-${inst.sequence}`}>
                      <TableCell>{inst.sequence}</TableCell>
                      <TableCell>{formatAUDate(inst.due_date)}</TableCell>
                      <TableCell>{fmtMoney(inst.amount)}</TableCell>
                      <TableCell><Badge className={`${instColors[inst.status]} text-xs capitalize`}>{inst.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        {inst.status !== 'paid' && (
                          <Button size="sm" variant="outline" onClick={() => handleInstalment(inst.id, 'paid')}>Mark paid</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      {/* B#18 — Correspondence log */}
      <Card data-testid="debt-correspondence-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <Mail className="w-5 h-5" /> Correspondence Log
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => setShowCorrForm((v) => !v)} data-testid="toggle-correspondence-form">
            <Plus className="w-4 h-4 mr-1" /> Log entry
          </Button>
        </CardHeader>
        <CardContent>
          {showCorrForm && (
            <form onSubmit={handleAddCorrespondence} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5 p-4 bg-muted/50 rounded-md">
              <div className="space-y-1">
                <Label className="text-xs">Direction</Label>
                <Select value={corrForm.direction} onValueChange={(v) => setCorrForm({ ...corrForm, direction: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="incoming">Incoming (from ATO)</SelectItem>
                    <SelectItem value="outgoing">Outgoing (to ATO)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select value={corrForm.correspondence_type} onValueChange={(v) => setCorrForm({ ...corrForm, correspondence_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CORRESPONDENCE_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs">Subject</Label>
                <Input value={corrForm.subject} onChange={(e) => setCorrForm({ ...corrForm, subject: e.target.value })} placeholder="e.g. Director Penalty Notice received" />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs">Summary (optional)</Label>
                <Textarea rows={2} value={corrForm.summary} onChange={(e) => setCorrForm({ ...corrForm, summary: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Date</Label>
                <Input type="date" value={corrForm.occurred_on} onChange={(e) => setCorrForm({ ...corrForm, occurred_on: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">ATO reference (optional)</Label>
                <div className="flex gap-2">
                  <Input value={corrForm.reference} onChange={(e) => setCorrForm({ ...corrForm, reference: e.target.value })} />
                  <Button type="submit" className="bg-primary hover:bg-primary/90">Save</Button>
                </div>
              </div>
            </form>
          )}
          {correspondence.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No correspondence logged yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {correspondence.map((c) => (
                  <TableRow key={c.id} data-testid={`correspondence-${c.id}`}>
                    <TableCell>{formatAUDate(c.occurred_on)}</TableCell>
                    <TableCell><Badge className="bg-muted text-foreground/90 text-xs">{CORRESPONDENCE_TYPE_LABELS[c.correspondence_type] || c.correspondence_type}</Badge></TableCell>
                    <TableCell className="capitalize">{c.direction}</TableCell>
                    <TableCell>
                      <div className="font-medium text-foreground">{c.subject}</div>
                      {c.summary && <div className="text-xs text-muted-foreground">{c.summary}</div>}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{c.reference || '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteCorrespondence(c.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
